export function sanitizeApiErrorMessage(rawMessage: string): string {
  const withoutHtml = rawMessage.replace(/<[^>]*>/g, '\n');

  const sqlExceptionMatch = withoutHtml.match(/SqlException\s*\([^)]*\)\s*:\s*([^\r\n]+)/i);
  if (sqlExceptionMatch?.[1]) {
    const sqlMessage = sqlExceptionMatch[1].replace(/\s+/g, ' ').trim();
    if (sqlMessage) {
      return sqlMessage.length > 220 ? `${sqlMessage.slice(0, 220).trim()}...` : sqlMessage;
    }
  }

  const lines = withoutHtml
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0);

  const stackOrDiagnosticLine = /^(at\s+|Microsoft\.|--- End of stack trace|HEADERS\s*=+|Http failure response for\s+|ClientConnectionId\s*:|Error Number\s*:|State\s*:|Class\s*:|Accept\s*:|Connection\s*:|Host\s*:|User-Agent\s*:|Content-Type\s*:|Origin\s*:|Referer\s*:|Sec-Fetch-|sec-ch-ua|Accept-Encoding\s*:|Accept-Language\s*:|Content-Length\s*:)/i;
  const stackOrDiagnosticText = /(stack trace|ExceptionHandlerMiddleware|DeveloperExceptionPageMiddleware|ControllerActionInvoker|ResourceInvoker)/i;

  const usefulLines = lines.filter(
    (line) => !stackOrDiagnosticLine.test(line) && !stackOrDiagnosticText.test(line)
  );

  const baseCandidate = usefulLines[0] ?? lines[0] ?? '';
  let candidate = baseCandidate
    .replace(/\s+/g, ' ')
    .trim();

  if (!candidate) {
    return '';
  }

  if (/^Http failure response for\s+/i.test(candidate)) {
    return '';
  }

  return candidate.length > 220 ? `${candidate.slice(0, 220).trim()}...` : candidate;
}

export function collectApiErrorMessages(error: any): string[] {
  const messages: string[] = [];
  const seen = new Set<string>();

  const tryParseJsonString = (value: string): unknown => {
    const trimmed = value.trim();
    if (!trimmed || (!trimmed.startsWith('{') && !trimmed.startsWith('['))) {
      return undefined;
    }

    try {
      return JSON.parse(trimmed);
    } catch {
      return undefined;
    }
  };

  const pushMessage = (value: unknown): void => {
    if (typeof value !== 'string') {
      return;
    }

    const normalized = sanitizeApiErrorMessage(value);
    if (!normalized || seen.has(normalized)) {
      return;
    }

    seen.add(normalized);
    messages.push(normalized);
  };

  const flatten = (value: unknown): void => {
    if (value === null || value === undefined) {
      return;
    }

    if (typeof value === 'string') {
      const parsed = tryParseJsonString(value);
      if (parsed !== undefined) {
        flatten(parsed);
        return;
      }

      pushMessage(value);
      return;
    }

    if (Array.isArray(value)) {
      value.forEach(flatten);
      return;
    }

    if (typeof value === 'object') {
      const obj = value as Record<string, unknown>;

      pushMessage(obj['message']);
      pushMessage(obj['detail']);
      pushMessage(obj['title']);
      pushMessage(obj['error_description']);
      pushMessage(obj['error']);
      pushMessage(obj['exceptionMessage']);

      const errors = obj['errors'];
      if (errors && typeof errors === 'object') {
        Object.values(errors as Record<string, unknown>).forEach(flatten);
      }
    }
  };

  flatten(error?.error);
  pushMessage(error?.message);

  return messages;
}

export function extractApiErrorMessage(error: any, fallbackMessage = 'Request failed. Please try again.'): string {
  const messages = collectApiErrorMessages(error);
  if (messages.length > 0) {
    return messages.join(' ');
  }

  const statusText = String(error?.statusText ?? '').trim();
  if (statusText && statusText.toLowerCase() !== 'unknown error') {
    return statusText;
  }

  return fallbackMessage;
}

export function formatHttpErrorMessage(prefix: string, error: any): string {
  const extractedMessage = extractApiErrorMessage(error);
  return `${prefix}${extractedMessage ? ` (${extractedMessage})` : ''}`;
} 
